import L from 'leaflet';
import theme from '../../../theme';
import {
  STATUS_ACCENT,
  STATUS_TIER_LABEL,
  STOP_DISC_FILL,
} from './statusStyles';
import { formatDeltaLabel } from '../../../utils/displayFormat';

// Leaflet divIcon builders for the Monitoring route map. Inline styles
// throughout: Leaflet renders these HTML strings outside React's tree, so
// emotion class names from the parent don't reach them. The stop icon's halo
// uses an alpha-suffixed color (e.g. `${routeLine}2E`), the 8-digit hex form
// of the color at ~18% opacity.
//
// The map draws in one line color (`routeLine`, navigation blue): the driven
// trail, its dots, the trail start and the gap diamonds. Stop discs are ink
// (`markerInk`, green when stocked) and only the severely-late driver turns
// red.

const HTML_ESCAPES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

// Driver names come from emails, which Leaflet would render as raw HTML.
function escapeHtml(text) {
  return String(text).replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);
}

// Driver marker: a haloed glyph in the route line color — red once the driver
// is severely late (the caller decides via `severe`) — with a first-name
// badge. With a finite `bearing` (degrees clockwise from north) the glyph is a
// chevron pointing that way; without one it's a plain dot, since pings carry
// no heading.
export function makeDriverIcon(
  driver,
  { severe = false, bearing = null } = {},
) {
  // Colored by the route's status tier, exactly as before this feature
  // (green / amber / red / grey); `severe` (more than 30 min behind) forces
  // red on top, which the ticket asks for by name.
  const accent = severe
    ? theme.colors.statusOffTrack
    : STATUS_ACCENT[driver.status] || theme.colors.statusNeutral;
  const firstName = (driver.driverName || '').split(' ')[0] || 'Driver';
  const etaLabel =
    driver.etaDeltaMin === null || driver.etaDeltaMin === undefined
      ? STATUS_TIER_LABEL[driver.status] || ''
      : formatDeltaLabel(driver.etaDeltaMin, { zeroLabel: null });
  const rotate = Number.isFinite(bearing)
    ? `transform:rotate(${bearing}deg);`
    : '';
  const glyph = Number.isFinite(bearing)
    ? `<path d="M15 4 L24 24 L15 18.5 L6 24 Z" fill="${accent}"
         stroke="${theme.colors.white}" stroke-width="1.8" stroke-linejoin="round" />`
    : `<circle cx="15" cy="15" r="9" fill="${accent}"
         stroke="${theme.colors.white}" stroke-width="1.5" />`;

  const html = `
    <div style="position:relative;width:36px;height:36px;">
      <span style="
        position:absolute;left:50%;top:50%;
        width:38px;height:38px;
        margin:-19px 0 0 -19px;
        border-radius:${theme.radii.circle};
        background:${accent};
        opacity:0.22;
        animation:mt-halo-pulse 2.2s ease-in-out infinite;
      "></span>
      <svg
        viewBox="0 0 30 30"
        width="22"
        height="22"
        style="
          position:absolute;left:50%;top:50%;
          margin:-11px 0 0 -11px;
          filter:${theme.shadows.markerGlyph};
          ${rotate}
        "
      >${glyph}</svg>
      <span style="
        position:absolute;left:50%;top:38px;
        transform:translateX(-50%);
        padding:2px 6px;
        background:${theme.colors.white};
        color:${accent};
        border:1px solid ${accent};
        border-radius:${theme.radii.pill};
        font-size:${theme.sizes.microFontSize};
        font-weight:600;
        white-space:nowrap;
        font-family:${theme.fonts.system};
        line-height:1.2;
        box-shadow:${theme.shadows.markerLabel};
      ">${escapeHtml(firstName)} · ${etaLabel}</span>
    </div>
  `;

  return L.divIcon({
    html,
    className: 'mt-driver-marker',
    iconSize: [36, 56],
    iconAnchor: [18, 18],
    popupAnchor: [0, -16],
  });
}

// Single-route stop marker. `fill` is the stop's display state (a
// STOP_DISC_FILL key), `pulse` draws a pulsing halo behind the stop in flight
// — the only state that gets one; every other disc is a flat circle with a
// white ring — and `label` is the number shown ('' or null for none).
export function makeRouteStopIcon({
  label = '',
  fill = 'pending',
  pulse = false,
} = {}) {
  const state = STOP_DISC_FILL[fill] || STOP_DISC_FILL.pending;
  const halo = pulse
    ? `<span style="
        position:absolute;inset:0;
        border-radius:${theme.radii.circle};
        background:${state.disc}2E;
        animation:mt-halo-pulse 2.2s ease-in-out infinite;
      "></span>`
    : '';
  // Filled discs wear a white ring so they pop off the route line; the hollow
  // (pending) disc IS a white circle, so its ring is the ink border itself.
  const disc = state.hollow
    ? `background:${theme.colors.white};color:${state.text};border:2.5px solid ${state.disc};`
    : `background:${state.disc};color:${state.text};border:2px solid ${theme.colors.white};`;
  const text = label === null || label === undefined ? '' : label;
  const html = `
    <div style="position:relative;width:36px;height:36px;">
      ${halo}
      <span style="
        position:absolute;left:50%;top:50%;
        width:26px;height:26px;
        margin:-13px 0 0 -13px;
        box-sizing:border-box;
        border-radius:${theme.radii.circle};
        ${disc}box-shadow:${theme.shadows.stopGlyph};
        display:flex;align-items:center;justify-content:center;
        font-size:${theme.sizes.microFontSize};font-weight:700;line-height:1;
        font-family:${theme.fonts.system};
      ">${text}</span>
    </div>
  `;
  return L.divIcon({
    html,
    className: 'mt-stop-marker',
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -16],
  });
}

// Start-of-trail marker: a small hollow ring (white center, route-line border)
// at the first recorded position of a driver's driven path, so the trail's
// starting point reads distinctly from every other marker on the map.
export function makeTrailStartIcon() {
  const html = `
    <div style="
      width:14px;height:14px;
      box-sizing:border-box;
      border-radius:${theme.radii.circle};
      background:${theme.colors.white};
      border:3px solid ${theme.colors.routeLine};
      box-shadow:${theme.shadows.stopGlyph};
    "></div>
  `;
  return L.divIcon({
    html,
    className: 'mt-start-marker',
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

// Gap marker: a small hollow diamond in the route line color, anchored halfway
// along a stretch of trail the phone never reported. Hollow and diamond-
// shaped so it reads as "no data here" rather than as a recorded position.
export function makeGapIcon() {
  const html = `
    <div style="
      width:12px;height:12px;
      box-sizing:border-box;
      background:${theme.colors.white};
      border:2px solid ${theme.colors.routeLine};
      transform:rotate(45deg);
      box-shadow:${theme.shadows.stopGlyph};
    "></div>
  `;
  return L.divIcon({
    html,
    className: 'mt-gap-marker',
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
}
